import { useState } from "react";
import { motion } from "framer-motion";
import { AlertCircle, CheckCircle2, Send } from "lucide-react";
import { getCookie } from "../../lib/site";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/newsletter`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const NewsletterForm = ({ idPrefix }) => {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!EMAIL_RE.test(email.trim())) {
      setError("Podaj poprawny adres e-mail.");
      return;
    }
    if (!consent) {
      setError("Zaznacz wymaganą zgodę, aby się zapisać.");
      return;
    }
    const eventId = crypto.randomUUID();
    setStatus("loading");
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          event_id: eventId,
          event_source_url: window.location.href,
          fbp: getCookie("_fbp"),
          fbc: getCookie("_fbc"),
        }),
      });
      if (!res.ok) throw new Error("subscribe failed");
      localStorage.setItem("newsletterSubscribed", "1");
      if (window.fbq) window.fbq("track", "Lead", {}, { eventID: eventId });
      setStatus("success");
      setEmail("");
      setConsent(false);
    } catch {
      setStatus("error");
      setError("Nie udało się zapisać adresu. Spróbuj ponownie.");
    }
  };

  if (status === "success") {
    return (
      <motion.p
        data-testid={`${idPrefix}-success-message`}
        className="flex items-start gap-3 rounded-2xl border border-brick/30 bg-brick/5 p-5 text-sm font-medium text-choc sm:text-base"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brick" />
        Dziękujemy! Twój adres e-mail został zapisany.
      </motion.p>
    );
  }

  return (
    <form data-testid={`${idPrefix}-form`} onSubmit={submit} noValidate>
      <label
        htmlFor={`${idPrefix}-email`}
        className="text-xs font-semibold uppercase tracking-wider text-choc"
      >
        Adres e-mail
      </label>
      <div className="mt-2.5 flex flex-col gap-3 sm:flex-row">
        <input
          id={`${idPrefix}-email`}
          data-testid={`${idPrefix}-email-input`}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="twoj@email.pl"
          autoComplete="email"
          className="w-full flex-1 rounded-full border border-sand bg-cream/60 px-5 py-4 text-sm text-choc outline-none transition-colors duration-300 placeholder:text-clay/50 focus:border-brick"
        />
        <button
          data-testid={`${idPrefix}-submit-button`}
          type="submit"
          disabled={status === "loading"}
          className="group inline-flex shrink-0 items-center justify-center gap-2.5 rounded-full bg-choc px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-choc-deep disabled:cursor-not-allowed disabled:opacity-50"
        >
          {status === "loading" ? "Zapisywanie…" : "Zapisuję się"}
          <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </div>

      <label
        data-testid={`${idPrefix}-consent-label`}
        className="mt-5 flex cursor-pointer items-start gap-3"
      >
        <input
          data-testid={`${idPrefix}-consent-checkbox`}
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-5 w-5 shrink-0 accent-[#B23A2F]"
        />
        <span className="text-xs leading-relaxed text-clay sm:text-sm">
          Wyrażam zgodę na otrzymywanie drogą elektroniczną informacji
          marketingowych. Zgodę mogę wycofać w dowolnym momencie.
        </span>
      </label>

      {error && (
        <p
          data-testid={`${idPrefix}-error-message`}
          role="alert"
          className="mt-4 flex items-start gap-2 text-sm font-medium text-brick"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {error}
        </p>
      )}
    </form>
  );
};
